import "date-fns";
import React, { useEffect, useState } from "react";
import WizardButtons from './WizardButtons';
import { useForm, useFormState } from "react-hook-form";
import DateFnsUtils from "@date-io/date-fns";
import {
    Grid,
    Typography,
    Button,
    IconButton,
    Tooltip,
    TextField,
    FormControl,
    MenuItem
} from "@material-ui/core";
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';

import { CloudDownload } from "@material-ui/icons";
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import {
    MuiPickersUtilsProvider,
    KeyboardDatePicker
} from "@material-ui/pickers";
import { setWorkIncapacityForm } from "../../application/actions";
import { useAppState } from "../../application/AppState";
import useTranslation from "../../application/translation";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { InputFiles } from "../../application/helpers/InputFiles";
import { getDefaultValue2 } from "../../application/helpers/form";
import { claimTypeEnum } from "../../application/enum";

export default function FormWorkIncapacity({ handleBack, handleNext }) {
    const [{ formValues, claimDate, language }, dispatch] = useAppState();
    const translation = useTranslation();

    const datePickerTheme = createMuiTheme({
    overrides: {
    MuiPickersCalendarHeader: {
    dayLabel: {
    color: '#000000',   // svart text
    fontWeight: 600,    // fetare för bättre läsbarhet
    }
    }
    }
    });

    const formType = FormWorkIncapacity.name;
    const initClaimType = getDefaultValue2(formValues, formType, "typeSelector") ?? claimTypeEnum.Accident;
    const [claimType, setClaimType] = useState(initClaimType);

    const errorObj = {
        required_error: translation.error_mandatory,
        invalid_type_error: translation.error_invalidentry,
    };
    const textRequired = { message: translation.error_mandatory };

    const accidentSchema = z.object({
        typeSelector: z.literal(claimTypeEnum.Accident),
        conditionStartDate: z.date(errorObj),
        accidentLocation: z.string().min(1, textRequired),
        description: z.string().min(1, textRequired),
        doctorNoticeDate: z.date(errorObj),
        doctorNameEmail: z.string().min(1, textRequired),
    }).passthrough();

    const diseaseSchema = z.object({
        typeSelector: z.literal(claimTypeEnum.Disease),
        conditionStartDate: z.date(errorObj),
        description: z.string().min(1, textRequired),
        doctorNoticeDate: z.date(errorObj),
        doctorNameEmail: z.string().min(1, textRequired),
    }).passthrough();

    const formSchema = z.union([accidentSchema, diseaseSchema]);

    const { formState, getValues, handleSubmit, register, setValue, control, reset } = useForm({
        mode: "onChange",
        defaultValues: {
            typeSelector: initClaimType,
            conditionStartDate: getDefaultValue2(formValues, formType, "conditionStartDate"),
            accidentLocation: getDefaultValue2(formValues, formType, "accidentLocation"),
            firstSickLeaveDate: claimDate,
            lastSickLeaveDate: getDefaultValue2(formValues, formType, "lastSickLeaveDate"),
            description: getDefaultValue2(formValues, formType, "description"),
            doctorNoticeDate: getDefaultValue2(formValues, formType, "doctorNoticeDate"),
            doctorNameEmail: getDefaultValue2(formValues, formType, "doctorNameEmail"),
            sickLeaveCertFile: getDefaultValue2(formValues, formType, "sickLeaveCertFile"),
            socialSecurityCertFile: getDefaultValue2(formValues, formType, "socialSecurityCertFile"),
            employerCertFile: getDefaultValue2(formValues, formType, "employerCertFile"),
            medicalCertFile: getDefaultValue2(formValues, formType, "medicalCertFile"),
            medicalJournalFile: getDefaultValue2(formValues, formType, "medicalJournalFile"),
        },
        resolver: zodResolver(formSchema),
    });

    const { errors } = useFormState({ control });

    // useEffect(() => {
    //     reset();
    // }, [reset]);

    const onSubmit = (data) => {
        dispatch(setWorkIncapacityForm(data));
        handleNext();
    };

    const handleClaimTypeChange = (e) => {
        setClaimType(e.target.value);
    };

    const handleDateByName = (date, name) => {
        setValue(name, date, { shouldValidate: true });
    };

    const values = getValues();

    return (
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <ThemeProvider theme={datePickerTheme}>                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <TextField
                                id="typeSelector"
                                {...register("typeSelector")}
                                select
                                variant="outlined"
                                label={translation.sick_type}
                                value={claimType}
                                onChange={handleClaimTypeChange}
                                inputProps={{ 'aria-label': translation.sick_type }}
                            >
                                <MenuItem value={claimTypeEnum.Accident}>{translation.sick_accident}</MenuItem>
                                <MenuItem value={claimTypeEnum.Disease}>{translation.sick_sick}</MenuItem>
                            </TextField>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} />

                    <Grid item xs={12} sm={6}>
                        <KeyboardDatePicker
                            id="conditionStartDate"
                            label={claimType === claimTypeEnum.Disease ? translation.sick_sickwhen : translation.sick_accidentdate}
                            format={translation.dateformat}
                            margin="normal"
                            inputVariant="outlined"
                            value={values.conditionStartDate || null}
                            onChange={(date) => handleDateByName(date, "conditionStartDate")}
                            disableFuture
                            KeyboardButtonProps={{ "aria-label": translation.sick_accidentdate }}
                            leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                            rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                            error={!!errors.conditionStartDate}
                            helperText={errors.conditionStartDate?.message}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        {claimType === claimTypeEnum.Accident && (
                            <TextField
                                id="accidentLocation"
                                {...register("accidentLocation")}
                                label={translation.sick_accidentlocation}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                inputProps={{ 'aria-label': translation.sick_accidentlocation }}
                                error={!!errors.accidentLocation}
                                helperText={errors.accidentLocation?.message}
                            />
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <KeyboardDatePicker
                            id="firstSickLeaveDate"
                            label={translation.sick_firstsickday}
                            format={translation.dateformat}
                            margin="normal"
                            inputVariant="outlined"
                            value={claimDate}
                            disabled
                            KeyboardButtonProps={{ "aria-label": translation.sick_firstsickday }}
                            leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                            rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <KeyboardDatePicker
                            id="lastSickLeaveDate"
                            label={translation.sick_lastsickday}
                            format={translation.dateformat}
                            margin="normal"
                            inputVariant="outlined"
                            value={values.lastSickLeaveDate || null}
                            onChange={(date) => handleDateByName(date, "lastSickLeaveDate")}
                            KeyboardButtonProps={{ "aria-label": translation.sick_lastsickday }}
                            leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                            rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                            error={!!errors.lastSickLeaveDate}
                            helperText={
                                errors.lastSickLeaveDate
                                    ? errors.lastSickLeaveDate.message
                                    : translation.hospital_emptyoptional
                            }
                            FormHelperTextProps={{ style: { fontSize: '0.75rem' } }}                        />
                        {/*<Typography variant="caption">{translation.hospital_emptyoptional}</Typography>*/}
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            id="description"
                            {...register("description")}
                            label={claimType === claimTypeEnum.Disease ? translation.sick_symptoms : translation.sick_accidenthow}
                            variant="outlined"
                            fullWidth
                            multiline
                            minRows={3}
                            margin="normal"
                            inputProps={{ 'aria-label': translation.sick_symptoms }}
                            error={!!errors.description}
                            helperText={errors.description?.message}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <KeyboardDatePicker
                            id="doctorNoticeDate"
                            label={translation.sick_firstdaydoctor}
                            format={translation.dateformat}
                            margin="normal"
                            inputVariant="outlined"
                            value={values.doctorNoticeDate || null}
                            onChange={(date) => handleDateByName(date, "doctorNoticeDate")}
                            KeyboardButtonProps={{ "aria-label": translation.sick_firstdaydoctor }}
                            leftArrowButtonProps={{ 'aria-label': 'Föregående månad' }}
                            rightArrowButtonProps={{ 'aria-label': 'Nästa månad' }}
                            error={!!errors.doctorNoticeDate}
                            helperText={errors.doctorNoticeDate?.message}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            id="doctorNameEmail"
                            {...register("doctorNameEmail")}
                            label={translation.sick_doctornameadress}
                            variant="outlined"
                            fullWidth
                            multiline
                            minRows={5}
                            margin="normal"
                            inputProps={{ 'aria-label': translation.sick_doctornameadress }}
                            error={!!errors.doctorNameEmail}
                            helperText={errors.doctorNameEmail?.message}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1">
                            {translation.file_attatchmentsInfo}
                            <Tooltip
                                title={
                                    <span>
                                        <strong>{translation.file_tooltip1}</strong><br /><br />
                                        {translation.file_tooltip2}<br /><br />
                                        {translation.file_tooltip3}
                                    </span>
                                }
                                aria-label="filbilaga-information"
                            >
                                <IconButton aria-label="filbilaga-information">
                                    <HelpOutlineIcon />
                                </IconButton>
                            </Tooltip>
                        </Typography>
                        <Typography variant="body2">{translation.file_attachmentsInfo2}</Typography><br />
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant="body2">{translation.sick_file_sickcert}</Typography>
                        <InputFiles fieldName="sickLeaveCertFile" errors={errors} register={register} formType={formType} />
                    </Grid>

                    {language !== "no" && (
                        <Grid item xs={6}>
                            <Typography variant="body2">{translation.sick_file_socialsecurity}</Typography>
                            <InputFiles fieldName="socialSecurityCertFile" errors={errors} register={register} formType={formType} />
                        </Grid>
                    )}

                    <Grid item xs={6}>
                        <Typography variant="body2">{translation.sick_file_workcert}</Typography>
                        <InputFiles fieldName="employerCertFile" errors={errors} register={register} formType={formType} />
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant="body2">{translation.sick_file_doctorcert}</Typography>
                        <InputFiles fieldName="medicalCertFile" errors={errors} register={register} formType={formType} />
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant="body2">{translation.sick_file_journal}</Typography>
                        <InputFiles fieldName="medicalJournalFile" errors={errors} register={register} formType={formType} />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2">{translation.sick_doctorsatt_info}</Typography>
                        <Button
                            variant="text"
                            startIcon={<CloudDownload color="primary" />}
                            target="_blank"
                            href={`${process.env.PUBLIC_URL}/Documents/${translation.doctorAttFile}`}
                            aria-label={translation.sick_doctorslink}
                        >
                            {translation.sick_doctorslink}
                        </Button>
                    </Grid>
                </Grid>
              </ThemeProvider>
              </MuiPickersUtilsProvider>
            <WizardButtons handleBack={handleBack} isStepValid={true} />
        </form>
    );
}
